/**
 * CurrencySelect Component
 * 
 * Dropdown selector for currencies with search support
 */

// External libraries
import { Check, ChevronsUpDown } from 'lucide-react';

// Internal - Components
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// Internal - Constants
import { CURRENCY_LIST } from '../../lib/constants/currencies';
import type { Currency } from '../../types/currency';

// Internal - Utils
import { cn } from '../ui/utils';
import { useState } from 'react';

export interface CurrencySelectProps {
  value: Currency;
  onChange: (value: Currency) => void;
  disabled?: boolean;
  className?: string;
}

export function CurrencySelect({
  value,
  onChange,
  disabled = false,
  className,
}: CurrencySelectProps) {
  const [open, setOpen] = useState(false);

  const selectedCurrency = CURRENCY_LIST.find((curr) => curr.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Selecionar moeda"
          disabled={disabled}
          className={cn('justify-between', className)}
        >
          {selectedCurrency ? (
            <span className="flex items-center gap-2">
              <span className="font-mono">{selectedCurrency.symbol}</span>
              <span>{selectedCurrency.code}</span>
              <span className="hidden md:inline text-muted-foreground">
                - {selectedCurrency.name}
              </span>
            </span>
          ) : (
            'Selecione uma moeda'
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full md:w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar moeda..." />
          <CommandEmpty>Nenhuma moeda encontrada</CommandEmpty>
          <CommandGroup>
            {CURRENCY_LIST.map((currency) => (
              <CommandItem
                key={currency.code}
                value={currency.code}
                onSelect={(currentValue) => {
                  onChange(currentValue.toUpperCase() as Currency);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === currency.code ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="font-mono w-8">{currency.symbol}</span>
                  <span className="w-12">{currency.code}</span>
                  <span className="text-muted-foreground flex-1">
                    {currency.name}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
